<script setup lang="ts">
import { SitePayloadLink } from '#components'

const sharedData = useState('sharedData')


type HomeContentBlock = NonNullable<HomePage['content']>[number]

// The GraphQL query (queries/global/home-page.ts) always resolves the upload
// relations, so narrow the generated `string | MaskShape` unions to the document.
// core-mask-shape-with-text needs the file fields non-null on top of that.
type HomeMaskItemEntry = Extract<HomeContentBlock, { blockType: 'home-mask-item' }> & {
  maskShape: MaskShape & { filename: string, url: string, width: number, height: number }
}

type HomeImageItemEntry = Extract<HomeContentBlock, { blockType: 'home-image-item' }> & {
  image: Media
}

type HomeContentEntry = HomeMaskItemEntry | HomeImageItemEntry

// What the query actually returns: textColor arrives under a per-block alias
// (see queries/global/home-page.ts) and is folded back below.
type TextColor = HomeContentEntry['textColor']
type HomeContentWireEntry = Omit<HomeContentEntry, 'textColor'> & {
  maskTextColor?: TextColor | null
  imageTextColor?: TextColor | null
}

const props = defineProps<{
  data: HomePage
}>()

// Deactivated items stay in the global (the editor can switch them back on)
// but never reach the page.
const content = computed(() =>
  ((props.data.content ?? []) as HomeContentWireEntry[])
    .filter(item => !item.deactivated)
    .map(({ maskTextColor, imageTextColor, ...item }): HomeContentEntry => ({
      ...item,
      // Items saved before the field existed carry no value; that reads as auto.
      textColor: maskTextColor ?? imageTextColor ?? 'auto',
    } as HomeContentEntry)),
)

// Both grids share the colour opt-in (utils/item-colors.ts); what differs here
// is the default the mask items fall back to, the neutral gray, and the gray
// hover marking a linked item as clickable. A coloured hover is the editor
// asking for it outright and stands on its own.
const maskBackgroundColor = (item: HomeMaskItemEntry) =>
  maskRestColors(item).background || 'var(--color--gray)'

const maskHoverColor = (item: HomeMaskItemEntry) =>
  maskHoverColors(item).background || (hasPayloadLink(item.link) ? 'var(--color--gray)' : null)

// Same rule the mask shapes use for their label: black or white, whichever
// reads better on the colour the editor picked — unless the editor pinned it.
const hoverTextStyle = (item: HomeImageItemEntry) => {
  const background = chosenBackgroundColor(item) || 'var(--color--gray)'

  return {
    backgroundColor: background,
    color: forcedTextColor(item) || contrastTextColor(background, { fallback: 'currentColor' }),
    // Insets the panel itself, so the colour can sit over part of the image
    // rather than over the whole tile.
    ...marginStyle(item, { field: 'hoverTextMargin' }),
  }
}
</script>

<template>
  <div class="contents">
    <ul class="gap-1 lg:columns-2 mx-1 pt-3_5">
      <!-- Below sm the address scales with the viewport: "Stadhouderskade 86" is
           the widest line at 9.696em (measured with opentype.js against
           ateliers-sans-400.otf), so 95vw / 9.696 ≈ 9.8vw fills 95% of the
           viewport. 1.111 is the text-lg leading ratio (40/36, 50/45). -->
      <li class="-mt-3_5 pt-1 mb-1 text-lg break-inside-avoid">
        de ateliers<br />
        Stadhouderskade 86<br />
        Amsterdam
      </li>

      <li v-for="(item, index) in content" :key="item.id ?? index" class="break-inside-avoid">
        <component
          :is="hasPayloadLink(item.link) ? SitePayloadLink : 'div'"
          v-bind="hasPayloadLink(item.link) ? { data: item.link } : {}"
          class="block overflow-hidden relative group pointer-events-none grid-margin"
          :style="marginStyle(item)"
        >
          <core-mask-shape-with-text
            v-if="item.blockType === 'home-mask-item' && item.maskShape"
            :mask-shape="item.maskShape"
            :text="item.title"
            :background-color="maskBackgroundColor(item)"
            :hover-color="maskHoverColor(item)"
            :text-color="maskRestColors(item).text"
            :hover-text-color="maskHoverColors(item).text"
          />

          <core-image
            v-else-if="item.blockType === 'home-image-item' && item.image"
            :image="item.image"
            class="pointer-events-auto"
          />

          <!-- Fixed size rather than fitted: how much text fits the tile is the
               editor's call, and anything longer is clipped by the overflow. -->
          <div
            v-if="item.blockType === 'home-image-item' && hasRichTextContent(item.hoverText)"
            class="absolute inset-0 p-1 overflow-hidden opacity-0 group-hover:opacity-100 pointer-events-none grid-margin"
            :style="hoverTextStyle(item)"
          >
            <core-rich-text :content="item.hoverText" text-size-class="text-d md:text-md" />
          </div>
        </component>
      </li>
    </ul>

    <div 
      class="md:sticky md:bottom-0 pointer-events-none" 
      role="presentation"
    >
      <div class="px-1 pb-1 mt-3 -mb-1 text-d md:text-dm text-balance">
        {{ sharedData?.tagline }}
      </div>
    </div>

    <pages-home-alumni-activities />
  </div>
</template>
